import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { UPDATE_CAR, GET_CAR } from '../../services/carService/queries';
import { toast } from 'react-toastify';
import { Button } from '../button';
import { IGetCarQuery } from '../../services/carService/__generated__/queries.generated';

const Container = styled.div`
  ${tw`p-6 max-w-2xl`}
`;

const Header = styled.div`
  ${tw`flex items-center gap-2 mb-6`}
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold`}
`;

const InfoIcon = styled.span`
  ${tw`text-text-tertiary`}
`;

const Section = styled.div`
  ${tw`mb-6`}
`;

const SectionTitle = styled.h2`
  ${tw`text-base font-semibold mb-2`}
`;

const TextAreaWrapper = styled.div`
  ${tw`relative`}
`;

const TextArea = styled.textarea`
  ${tw`w-full p-4 border border-border-default rounded-md appearance-none bg-white min-h-[150px] resize-none focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent`}
`;

const CharCount = styled.div<{ isAtLimit: boolean }>`
  ${tw`text-right text-xs mt-1`}
  ${({ isAtLimit }) => (isAtLimit ? tw`text-red-500` : tw`text-text-secondary`)}
`;

const SaveButtonContainer = styled.div`
  ${tw`mt-4`}
`;

interface GuestInstructionsProps {
  carId: string;
  guestInstructions: string | null | undefined;
}

const GuestInstructions: React.FC<GuestInstructionsProps> = ({ carId, guestInstructions }) => {
  const [instructions, setInstructions] = useState(guestInstructions || '');
  const maxLength = 500;

  const { data, loading: carLoading } = useQuery<IGetCarQuery>(GET_CAR, {
    variables: { id: carId },
    skip: !carId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (data?.car?.guestInstructions !== undefined) {
      setInstructions(data.car.guestInstructions || '');
    }
  }, [data?.car?.guestInstructions]);

  const [updateCar, { loading }] = useMutation(UPDATE_CAR, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    onCompleted: () => {
      toast.success('Guest instructions updated successfully');
    },
    onError: error => {
      toast.error('Error updating guest instructions');
    },
  });

  const onSubmit = async () => {
    await updateCar({
      variables: {
        id: carId,
        updateCarInput: {
          guestInstructions: instructions.trim(),
        },
      },
    });
  };

  return (
    <Container>
      <Header>
        <Title>Guest Instructions</Title>
        <InfoIcon>ⓘ</InfoIcon>
      </Header>

      <Section>
        <SectionTitle>Instructions for your guests</SectionTitle>
        <TextAreaWrapper>
          <TextArea
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            placeholder="Help your guests by providing instructions for pick-up, drop-off, and any other details they should know."
            maxLength={maxLength}
          />
          <CharCount isAtLimit={instructions.length >= maxLength}>
            {instructions.length} / {maxLength} characters
          </CharCount>
        </TextAreaWrapper>
      </Section>

      <SaveButtonContainer>
        <Button
          theme="filled"
          text="Save"
          onClick={onSubmit}
          isDisabled={loading}
          isLoading={loading}
        />
      </SaveButtonContainer>
    </Container>
  );
};

export default GuestInstructions;
